import React, { useState, useEffect } from 'react';
import { api, useServer} from '../contexts/ServerContext';
import toast from 'react-hot-toast';
import { 
  Terminal, 
  Clock, 
  User, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Filter,
  Monitor,
  Eye,
  Gift,
  ArrowUpCircle,
  MessageSquareOff,
  MicOff,
  Skull,
  Ban,
  Check,
  Smile,
  Shield,
  Crown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { steamId64ToV2, getCachedDisplayName } from '../utils/helpers';
import ScreengrabLightbox from './ScreengrabLightbox';

function Commands() {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [screengrabModal, setScreengrabModal] = useState({ open: false, image: '', key: '' });
  const { server } = useServer();
  const { user } = useAuth();
  const isCurator = user && ['curator', 'yufuadmin', 'superadmin'].includes(user.rank);
  const isAdmin = user && user.rank && user.rank !== 'user';
  const [showASayModal, setShowASayModal] = useState(false);
  const [asayMessage, setASayMessage] = useState('');
  const [asayLoading, setASayLoading] = useState(false);

  const [showGivePifsModal, setShowGivePifsModal] = useState(false);
  const [givePifsSteamId, setGivePifsSteamId] = useState('');
  const [givePifsAmount, setGivePifsAmount] = useState('');
  const [givePifsLoading, setGivePifsLoading] = useState(false);
  const [givePifsTake, setGivePifsTake] = useState(false);

  const slayerRanks = [
    { value: 1, label: 'Midzunoto' },
    { value: 2, label: 'Midzunoe' },
    { value: 3, label: 'Kanoto' },
    { value: 4, label: 'Kanoe' },
    { value: 5, label: 'Tsuchinoto' },
    { value: 6, label: 'Tsuchinoe' },
    { value: 7, label: 'Hinoto' },
    { value: 8, label: 'Hinoe' },
    { value: 9, label: 'Kinoto' },
    { value: 10, label: 'Kinoe' },
    { value: 11, label: 'Hashira' },
  ];

  const demonRanks = [
    { value: 1, label: 'Otokuma' },
    { value: 2, label: 'Demon' },
    { value: 3, label: 'Keykon' },
    { value: 4, label: 'Hofuma' },
    { value: 5, label: 'Chisuma' },
    { value: 6, label: 'Chikuma' },
    { value: 7, label: 'Asakuma' },
    { value: 8, label: 'Hobura' },
    { value: 9, label: 'Shateigashira' },
    { value: 10, label: 'Wakagashira' },
    { value: 11, label: 'Tsukomon' },
    { value: 12, label: 'Shiko' },
  ];

  const [showWhitelistModal, setShowWhitelistModal] = useState(false);
  const [whitelistSteamId, setWhitelistSteamId] = useState('');
  const [whitelistProfession, setWhitelistProfession] = useState('');
  const [whitelistLoading, setWhitelistLoading] = useState(false);

  const [showSetRankModal, setShowSetRankModal] = useState(false);
  const [setRankSteamId, setSetRankSteamId] = useState('');
  const [selectedFaction, setSelectedFaction] = useState('');
  const [selectedRankNumber, setSelectedRankNumber] = useState('');
  const [setRankLoading, setSetRankLoading] = useState(false);

  const [names, setNames] = useState({});

  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter]);

  useEffect(() => {
    fetchCommands();
  }, [currentPage, server, typeFilter]);

  useEffect(() => {
    const ids = [];
    commands.forEach(cmd => {
      if (cmd.executedBy && typeof cmd.executedBy === 'object' && cmd.executedBy.steamId) {
        ids.push(steamId64ToV2(cmd.executedBy.steamId));
      } else if (typeof cmd.executedBy === 'string') {
        ids.push(steamId64ToV2(cmd.executedBy));
      }
      if (cmd.target && typeof cmd.target === 'string' && cmd.target.startsWith('7656')) {
        ids.push(steamId64ToV2(cmd.target));
      }
    });
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    uniqueIds.forEach(async (steamId) => {
      if (!names[steamId]) {
        try {
          const displayName = await getCachedDisplayName(steamId, async () => {
            const resp = await api.get(`/api/players/${steamId}`);
            return resp.data.displayName || resp.data.name || steamId;
          });
          setNames(prev => ({ ...prev, [steamId]: displayName }));
        } catch {
          setNames(prev => ({ ...prev, [steamId]: steamId }));
        }
      }
    });
  }, [commands]);

  const fetchCommands = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        server: server
      });
      
      if (typeFilter) {
        params.append('type', typeFilter);
      }
      
      const response = await api.get(`/api/commands/history?${params}`, { withCredentials: true });
      setCommands(response.data.commands);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      toast.error('Ошибка при загрузке команд');
    } finally {
      setLoading(false);
    }
  };

  const handleGivePifs = async () => {
    if (!givePifsSteamId || !givePifsAmount || isNaN(Number(givePifsAmount))) {
      toast.error('Укажите корректные SteamID и количество');
      return;
    }
    setGivePifsLoading(true);
    try {
      await api.post(`/api/admin/give-pifs`, {
        target: givePifsSteamId,
        amount: Number(givePifsAmount),
        take: givePifsTake ? 1 : 0
      }, { withCredentials: true });
      toast.success('Юфики успешно выданы');
      setShowGivePifsModal(false);
      setGivePifsSteamId('');
      setGivePifsAmount('');
      setGivePifsTake(false);
      fetchCommands();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Ошибка при выдаче юфиков');
    } finally {
      setGivePifsLoading(false);
    }
  };

  const handleASay = async () => {
    if (!asayMessage.trim()) {
      toast.error('Введите сообщение');
      return;
    }
    setASayLoading(true);
    try {
      const adminName = user.displayName || user.steamId || 'Admin';
      const rank = user.rank || '';
      const command = `adminsay\"${adminName} (${rank})\"${asayMessage}`;
      await api.post(`/api/admin/action`, {
        action: 'adminsay',
        command,
      }, { withCredentials: true });
      toast.success('ASay отправлен');
      setShowASayModal(false);
      setASayMessage('');
      fetchCommands();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Ошибка при отправке ASay');
    } finally {
      setASayLoading(false);
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'kick':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'ban':
        return <Ban className="h-4 w-4 text-red-500" />;
      case 'unban':
        return <Smile className="h-4 w-4 text-green-500" />;
      case 'slay':
        return <Skull className="h-4 w-4 text-orange-500" />;
      case 'mute':
        return <MessageSquareOff className="h-4 w-4 text-orange-500" />;
      case 'gag':
        return <MicOff className="h-4 w-4 text-orange-500" />;
      case 'screengrab':
        return <Monitor className="h-4 w-4 text-red-500" />;
      case 'givepifs':
        return <Gift className="h-4 w-4 text-pink-500" />;
      case 'setrankid':
        return <ArrowUpCircle className="h-4 w-4 text-blue-500" />;
      case 'setrank':
        return <ArrowUpCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <Terminal className="h-4 w-4 text-blue-500" />;
    }
  };

  const getStatusIcon = (processed) => {
    return processed ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-yellow-500" />
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  const formatDuration = (duration) => {
    if (duration === 0) return 'Навсегда';
    const hours = Math.floor(duration / 60);
    const mins = duration % 60;
    return `${hours}ч ${mins}м`;
  };

  const extractScreengrabKey = (commandStr) => {
    // Формат: screengrab"STEAM_ID"KEY
    const match = commandStr.match(/screengrab\\?"[^"]+\\?"([a-fA-F0-9\-]+)/);
    return match ? match[1] : null;
  };

  const showScreengrab = async (commandStr) => {
    const key = extractScreengrabKey(commandStr);
    if (!key) {
      toast.error('Ключ скриншота не найден');
      return;
    }
    try {
      const res = await api.get(`/api/admin/screengrab?key=${key}`, { withCredentials: true });
      if (res.data && res.data.image) {
        setScreengrabModal({ open: true, image: res.data.image, key });
      } else {
        toast.error('Скриншот не найден');
      }
    } catch {
      toast.error('Ошибка при загрузке скриншота');
    }
  };

  const getCommandDisplay = (command) => {
    if (typeof command.command === 'string' && command.command.includes('"')) {
      const parts = command.command.split('"');
      // parts[0] — команда, остальные — параметры
      return parts.slice(1).filter(Boolean).join(' ').trim();
    }
    if (command.type === 'screengrab') {
      const match = command.command.match(/^(screengrab\"[^\"]+\")[^\"]+$/);
      if (match) return <>{match[1]}<Eye className="inline h-4 w-4 ml-1 align-text-bottom" /></>;
    }
    return command.command;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gmod-white ml-4">История команд</h1>
        <p className="text-gmod-white opacity-60 mt-2 ml-4">Просмотр всех выполненных команд и оффлайн команды</p>
      </div>

      {/* Filters */}
      <div className="bg-gmod-dark rounded-lg p-6 border border-gmod-lighterer">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-gmod-white opacity-60" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
          >
            <option value="">Все типы</option>
            <option value="kick">Кик</option>
            <option value="ban">Бан</option>
            <option value="slay">Убийство</option>
            <option value="mute">Мут</option>
            <option value="gag">Гаг</option>
            <option value="screengrab">Снимок экрана</option>
            <option value="unban">Снятие бана</option>
          </select>
        </div>
      </div>

      {(isCurator || isAdmin) && (
        <div className="bg-gmod-dark rounded-lg p-6 border border-gmod-lighterer flex items-center space-x-4 mb-4">
          {isCurator && (
            <button
              className="px-4 py-2 bg-theme-primary-hover text-gmod-white rounded hover:bg-theme-primary flex items-center justify-center transition-colors"
              onClick={() => setShowGivePifsModal(true)}
              title="Выдать юфики"
            >
              <Gift className="h-5 w-5" />
            </button>
          )}
          {isAdmin && (
            <button
              className="px-4 py-2 bg-gmod-green text-gmod-white rounded hover:bg-gmod-green hover:bg-opacity-80 flex items-center justify-center transition-colors"
              onClick={() => { setShowWhitelistModal(true); setWhitelistSteamId(''); setWhitelistProfession(''); }}
              title="Set WhiteList"
            >
              <Shield className="h-5 w-5" />
            </button>
          )}
          {isAdmin && (
            <button
              className="px-4 py-2 bg-gmod-yellow text-gmod-white rounded hover:bg-gmod-yellow hover:bg-opacity-80 flex items-center justify-center transition-colors"
              onClick={() => { setShowSetRankModal(true); setSetRankSteamId(''); setSelectedFaction(''); setSelectedRankNumber(''); }}
              title="Set Rank"
            >
              <Crown className="h-5 w-5" />
            </button>
          )}
          {isAdmin && (
            <button
              className="px-4 py-2 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover flex items-center justify-center transition-colors"
              onClick={() => setShowASayModal(true)}
              title="ASay"
            >
              <Terminal className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {/* Commands List */}
      <div className="bg-gmod-dark rounded-lg border border-gmod-lighterer">
        <div className="px-6 py-4 border-b border-gmod-lighterer">
          <h2 className="text-lg font-semibold text-gmod-white">Список команд</h2>
        </div>
        <div className="p-6">
          {commands.length === 0 ? (
            <p className="text-gmod-white opacity-60 text-center py-8">Команды не найдены</p>
          ) : (
            <div className="space-y-4">
              {commands.map((command) => (
                <div key={command._id} className="p-4 bg-gmod-lighter rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      {getActionIcon(command.type)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <p className="text-gmod-white font-medium">{command.type.toUpperCase()}</p>
                          {getStatusIcon(command.processed)}
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            command.processed 
                              ? 'bg-gmod-dark text-theme-primary border border-theme-primary' 
                              : 'bg-gmod-dark text-gmod-white border border-gmod-lightest'
                          }`}>
                            {command.processed ? 'Выполнено' : 'Ожидает'}
                          </span>
                        </div>
                        <p className="text-gmod-white opacity-80 text-sm mb-1">
                          <span className="text-gmod-white opacity-60">Цель:</span> {(() => {
                            let steamId = command.target;
                            if (command.target && typeof command.target === 'string' && command.target.startsWith('7656')) {
                              steamId = steamId64ToV2(command.target);
                            }
                            return steamId ? (
                              <a href={`/players/${steamId}`} className="text-theme-primary font-semibold hover:underline">{names[steamId] || steamId}</a>
                            ) : (command.target || 'Неизвестно');
                          })()}
                        </p>
                        <p className="text-gmod-white opacity-80 text-sm mb-1">
                          <span className="text-gmod-white opacity-60">Команда:</span> {getCommandDisplay(command)}
                        </p>
                        {command.reason && (
                          <p className="text-gmod-white opacity-80 text-sm mb-1">
                            <span className="text-gmod-white opacity-60">Причина:</span> {command.reason}
                          </p>
                        )}
                        {command.duration > 0 && (
                          <p className="text-gmod-white opacity-80 text-sm mb-1">
                            <span className="text-gmod-white opacity-60">Длительность:</span> {formatDuration(command.duration)}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gmod-white opacity-40 mt-2">
                          <div className="flex items-center space-x-1">
                            <User className="h-3 w-3" />
                            <span>
                              {(() => {
                                let steamId = null;
                                if (command.executedBy && typeof command.executedBy === 'object' && command.executedBy.steamId) {
                                  steamId = steamId64ToV2(command.executedBy.steamId);
                                } else if (typeof command.executedBy === 'string') {
                                  steamId = steamId64ToV2(command.executedBy);
                                }
                                return names[steamId] || steamId || 'Неизвестно';
                              })()}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>{formatDate(command.executedAt)}</span>
                          </div>
                        </div>
                        {command.processed && command.processedAt && (
                          <div className="flex items-center space-x-1 text-xs text-gmod-white opacity-40 mt-1">
                            <CheckCircle className="h-3 w-3" />
                            <span>Выполнено: {formatDate(command.processedAt)}</span>
                          </div>
                        )}
                        {command.result && (
                          <p className="text-gmod-white opacity-60 text-xs mt-1">
                            <span className="text-gmod-white opacity-40">Результат:</span> {command.result}
                          </p>
                        )}
                        {command.type === 'screengrab' && (
                          <button
                            className="mt-2 px-3 py-1 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover transition-colors flex items-center space-x-2"
                            onClick={() => showScreengrab(command.command)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            <span>Показать</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
          >
            Назад
          </button>
          <span className="px-4 py-2 text-gmod-white">
            {currentPage} из {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
          >
            Вперед
          </button>
        </div>
      )}
      {screengrabModal.open && (
        <ScreengrabLightbox
          image={screengrabModal.image}
          title="Скриншот команды"
          onClose={() => setScreengrabModal({ open: false, image: '', key: '' })}
        />
      )}
      {showGivePifsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gmod-dark rounded-lg p-6 w-full max-w-md border border-gmod-lighterer flex flex-col items-center">
            <h3 className="text-lg font-semibold text-gmod-white mb-4">Выдать юфики</h3>
            <input
              type="text"
              placeholder="SteamID"
              value={givePifsSteamId}
              onChange={e => setGivePifsSteamId(e.target.value)}
              className="mb-4 px-4 py-2 w-full bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
              disabled={givePifsLoading}
            />
            <input
              type="number"
              placeholder="Количество"
              value={givePifsAmount}
              onChange={e => setGivePifsAmount(e.target.value)}
              className="mb-4 px-4 py-2 w-full bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
              disabled={givePifsLoading}
            />
            <label className="flex items-center mb-4 select-none cursor-pointer group">
              <span className="relative inline-block w-6 h-6 mr-2 align-middle">
                <input
                  type="checkbox"
                  checked={givePifsTake}
                  onChange={e => setGivePifsTake(e.target.checked)}
                  className="absolute w-6 h-6 opacity-0 cursor-pointer z-10"
                  disabled={givePifsLoading}
                />
                <span className={`block w-6 h-6 rounded border-2 transition-colors duration-150 ${givePifsTake ? 'bg-theme-primary border-theme-primary' : 'bg-gmod-lighter border-gmod-lighterer'} group-hover:border-theme-primary`}></span>
                {givePifsTake && (
                  <Check className="absolute left-1 top-1 w-4 h-4 text-gmod-white pointer-events-none" />
                )}
              </span>
              <span className="text-gmod-white">Забрать?</span>
            </label>
            <div className="flex space-x-4 w-full">
              <button
                className="flex-1 px-4 py-2 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover"
                onClick={handleGivePifs}
                disabled={givePifsLoading || !givePifsSteamId || !givePifsAmount}
              >
                {givePifsLoading ? '...' : 'Выдать'}
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gmod-lighter text-gmod-white rounded hover:bg-theme-primary"
                onClick={() => setShowGivePifsModal(false)}
                disabled={givePifsLoading}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
      {showASayModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gmod-dark rounded-lg p-6 w-full max-w-md border border-gmod-lighterer flex flex-col items-center">
            <h3 className="text-lg font-semibold text-gmod-white mb-4">Сообщение всей администрации на сервере</h3>
            <textarea
              placeholder="Введите сообщение..."
              value={asayMessage}
              onChange={e => setASayMessage(e.target.value.slice(0, 50))}
              className="mb-2 px-4 py-2 w-full bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary min-h-[80px]"
              disabled={asayLoading}
              maxLength={50}
            />
            <div className="w-full text-right text-xs text-gmod-white opacity-60 mb-2">{asayMessage.length}/50</div>
            <div className="flex w-full gap-2">
              <button
                className="flex-1 h-10 px-4 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover transition-colors"
                onClick={handleASay}
                disabled={asayLoading || !asayMessage.trim() || asayMessage.length > 50}
              >
                {asayLoading ? '...' : 'Отправить'}
              </button>
              <button
                className="flex-1 h-10 px-4 bg-gmod-lighter text-gmod-white rounded hover:bg-theme-primary transition-colors"
                onClick={() => setShowASayModal(false)}
                disabled={asayLoading}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
      {showWhitelistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gmod-dark rounded-lg p-6 w-full max-w-md border border-gmod-lighterer">
            <h3 className="text-lg font-semibold text-gmod-white mb-4">Set WhiteList</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gmod-white opacity-60 mb-2">SteamID игрока</label>
                <input
                  type="text"
                  value={whitelistSteamId}
                  onChange={(e) => setWhitelistSteamId(e.target.value)}
                  className="w-full px-3 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  placeholder="Введите SteamID..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gmod-white opacity-60 mb-2">Название профессии</label>
                <input
                  type="text"
                  value={whitelistProfession}
                  onChange={(e) => setWhitelistProfession(e.target.value)}
                  className="w-full px-3 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  placeholder="Введите название профессии..."
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowWhitelistModal(false)}
                  className="flex-1 px-4 py-2 bg-gmod-lighterer text-gmod-white rounded-md hover:bg-gmod-lightest"
                >
                  Отмена
                </button>
                <button
                  onClick={async () => {
                    if (!whitelistSteamId.trim() || !whitelistProfession.trim()) return;
                    setWhitelistLoading(true);
                    try {
                      await api.post(`/api/commands`, {
                        command: `SetWhitelist"${whitelistSteamId.trim()}"${whitelistProfession.trim()}`,
                        target: whitelistSteamId.trim(),
                        reason: '',
                        duration: 0,
                        type: 'whitelist'
                      }, { withCredentials: true });
                      toast.success('Команда SetWhitelist отправлена');
                      setShowWhitelistModal(false);
                      setWhitelistSteamId('');
                      setWhitelistProfession('');
                      fetchCommands();
                    } catch (e) {
                      toast.error(e?.response?.data?.error || 'Ошибка при выполнении команды');
                    } finally {
                      setWhitelistLoading(false);
                    }
                  }}
                  disabled={whitelistLoading || !whitelistSteamId.trim() || !whitelistProfession.trim()}
                  className="flex-1 px-4 py-2 bg-theme-primary text-gmod-white rounded-md hover:bg-theme-primary-hover"
                >
                  {whitelistLoading ? '...' : 'Выполнить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSetRankModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gmod-dark rounded-lg p-6 w-full max-w-md border border-gmod-lighterer">
            <h3 className="text-lg font-semibold text-gmod-white mb-4">Set Rank</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gmod-white opacity-60 mb-2">SteamID игрока</label>
                <input
                  type="text"
                  value={setRankSteamId}
                  onChange={(e) => setSetRankSteamId(e.target.value)}
                  className="w-full px-3 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  placeholder="Введите SteamID..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gmod-white opacity-60 mb-2">Фракция</label>
                <select
                  value={selectedFaction}
                  onChange={(e) => { setSelectedFaction(e.target.value); setSelectedRankNumber(''); }}
                  className="w-full px-3 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
                >
                  <option value="">Выберите фракцию...</option>
                  <option value="slayer">Slayer</option>
                  <option value="demon">Demon</option>
                </select>
              </div>
              {selectedFaction && (
                <div>
                  <label className="block text-sm font-medium text-gmod-white opacity-60 mb-2">Ранг</label>
                  <select
                    value={selectedRankNumber}
                    onChange={(e) => setSelectedRankNumber(e.target.value)}
                    className="w-full px-3 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  >
                    <option value="">Выберите ранг...</option>
                    {(selectedFaction === 'slayer' ? slayerRanks : demonRanks).map(rank => (
                      <option key={rank.value} value={rank.value}>{rank.value} - {rank.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowSetRankModal(false)}
                  className="flex-1 px-4 py-2 bg-gmod-lighterer text-gmod-white rounded-md hover:bg-gmod-lightest"
                >
                  Отмена
                </button>
                <button
                  onClick={async () => {
                    if (!setRankSteamId.trim() || !selectedFaction || !selectedRankNumber) return;
                    
                    // Валидация SteamID
                    const steamId = setRankSteamId.trim();
                    if (!/^STEAM_[0-1]:[0-1]:[0-9]+$/.test(steamId)) {
                      toast.error('Неверный формат SteamID. Используйте формат STEAM_0:1:123456789');
                      return;
                    }
                    
                    setSetRankLoading(true);
                    try {
                      const rankLabel = (selectedFaction === 'slayer' ? slayerRanks : demonRanks)
                        .find(r => r.value === parseInt(selectedRankNumber))?.label || selectedRankNumber;
                      
                      // Сначала проверяем, существует ли игрок
                      try {
                        await api.get(`/api/players/${steamId}`, { withCredentials: true });
                      } catch (playerError) {
                        if (playerError.response?.status === 404) {
                          toast.error('Игрок с таким SteamID не найден');
                          return;
                        }
                      }
                      
                      await api.post(`/api/commands`, {
                        command: `SetFactionRank"${steamId}"${selectedFaction}"${rankLabel}`,
                        target: steamId,
                        reason: '',
                        duration: 0,
                        type: 'setrank'
                      }, { withCredentials: true });
                      
                      // Обновляем ранг в базе данных и локально
                      try {
                        const endpoint = selectedFaction === 'slayer' ? 'slayer-rank' : 'demon-rank';
                        console.log('Отправляем запрос на обновление ранга:', {
                          steamId: steamId,
                          endpoint,
                          rank: rankLabel
                        });
                        
                        await api.post(`/api/players/${steamId}/${endpoint}`, {
                          rank: rankLabel
                        }, { withCredentials: true });
                        
                        // Уведомляем другие компоненты об обновлении только после успешного сохранения
                        window.dispatchEvent(new CustomEvent('playerRankUpdated', {
                          detail: {
                            steamId: steamId,
                            faction: selectedFaction,
                            rank: rankLabel
                          }
                        }));
                      } catch (dbError) {
                        console.warn('Не удалось обновить ранг в базе данных:', dbError);
                        if (dbError.response?.status === 429) {
                          const retryAfter = dbError.response.data?.retryAfter || 60;
                          toast.error(`Слишком много запросов. Попробуйте через ${retryAfter} секунд`);
                        } else {
                          toast.error('Команда отправлена, но не удалось сохранить ранг в базе данных');
                        }
                        return;
                      }
                      
                      toast.success('Команда SetFactionRank отправлена');
                      setShowSetRankModal(false);
                      setSetRankSteamId('');
                      setSelectedFaction('');
                      setSelectedRankNumber('');
                      fetchCommands();
                    } catch (e) {
                      toast.error(e?.response?.data?.error || 'Ошибка при выполнении команды');
                    } finally {
                      setSetRankLoading(false);
                    }
                  }}
                  disabled={setRankLoading || !setRankSteamId.trim() || !selectedFaction || !selectedRankNumber}
                  className="flex-1 px-4 py-2 bg-theme-primary text-gmod-white rounded-md hover:bg-theme-primary-hover"
                >
                  {setRankLoading ? '...' : 'Выполнить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Commands; 